/**
 * Centralized Help & FAQ content for the Safety Center.
 * Three tiers of content:
 *   1. PLATFORM_HELP   — shared across all 3 apps (account, billing, safety, etc.)
 *   2. APP_HELP[appId] — community-specific guidance for Fryqnc, Pulsynq, MindLynq
 *
 * Tone: warm, direct, no jargon. Each item is a self-contained answer the
 * member can act on without leaving the page. RAELA can also surface these.
 */

import type { AppId } from "@/lib/apps";

export interface HelpItem {
  q: string;
  a: string;
}

export interface HelpSection {
  id: string;
  title: string;
  description?: string;
  items: HelpItem[];
}

// ─────────────────────────────────────────────────────────────────
// SHARED — every app, every member
// ─────────────────────────────────────────────────────────────────
export const PLATFORM_HELP: HelpSection[] = [
  {
    id: "account",
    title: "Account & Profile",
    description: "Sign in, your profile, and visibility.",
    items: [
      {
        q: "How do I create an account?",
        a: "Sign in with Google from the Sign-in page. We use your Google account only to verify your email — Wavlynq never posts to Google or pulls your contacts.",
      },
      {
        q: "Why do you ask for my legal name?",
        a: "Your legal name is used only for ID verification (KYC) and is never shown to other members. Your display name and username are what other members see.",
      },
      {
        q: "Can I change my username?",
        a: "Yes — go to Account → Public profile → Username. We check availability live. Changing it does not break links others have to your profile.",
      },
      {
        q: "What is Incognito mode?",
        a: "Incognito hides you from Discover and stops your profile from appearing in suggestion stacks. Existing matches and conversations are unaffected. Free on every tier.",
      },
      {
        q: "How do I delete my account?",
        a: "Account → bottom of the page → Delete account. This permanently removes your profile, photos, messages, and matches. Subscriptions are canceled automatically.",
      },
    ],
  },
  {
    id: "verification",
    title: "ID Verification (KYC)",
    description: "Required before discovery on every tier.",
    items: [
      {
        q: "Why do I need to verify my ID?",
        a: "Every member is verified before they can match. It cuts catfishing, age fraud, and scammers. You only do this once unless you change your legal name.",
      },
      {
        q: "What ID do I need?",
        a: "A government-issued photo ID — driver's license, passport, or state ID. You'll also do a quick liveness selfie. Powered by Stripe Identity.",
      },
      {
        q: "What happens to my ID document?",
        a: "It's processed by Stripe Identity for verification only. Wavlynq never stores the image of your ID — only a verified status flag and your name match result.",
      },
      {
        q: "My verification was rejected. What now?",
        a: "Common causes: blurry photo, expired ID, name mismatch with what you typed. Account → ID verification → try again. If it keeps failing, contact support@wavlynq.com.",
      },
    ],
  },
  {
    id: "matching",
    title: "Discovery & Matching",
    description: "How Discover, likes, and matches work.",
    items: [
      {
        q: "How does Discover work?",
        a: "We show you members who match your community, friendship lane (Dating, Friendship, Both, Mentorship), and are within your distance preference. Sort order is AMPLIFY → Priority → standard.",
      },
      {
        q: "What's the difference between a Like and a Super Signal?",
        a: "A Like is your standard interest signal. A Super Signal jumps you to the top of the other person's stack and includes an optional voice note. Super Signals are limited per day on Surge and unlimited on Spectrum/Attuned.",
      },
      {
        q: "Daily like limits?",
        a: "Signal: 25/day. Pulse: 1,000/day. Surge: 2,000/day. Spectrum and Attuned: unlimited.",
      },
      {
        q: "What is a Daily Signal?",
        a: "Once a day we pin one highly-compatible match to the top of your Discover stack. Your top compatibility pick of the day, every day.",
      },
      {
        q: "How long do matches last?",
        a: "Matches stay open for 7 days from when you matched. If neither of you sends a message, the match expires. Surge and above can extend matches.",
      },
    ],
  },
  {
    id: "messaging",
    title: "Messages & Voice",
    items: [
      {
        q: "Why can't I message someone I matched with?",
        a: "Free Signal members can receive messages but need Pulse or higher to send. This keeps cold-DM spam off the platform.",
      },
      {
        q: "Voice notes?",
        a: "Pulse: receive only. Surge and above: send and receive unlimited.",
      },
      {
        q: "RESONATE (MindLynq only)?",
        a: "RESONATE shares quick communication preferences before you talk — sensory needs, preferred message style, response time expectations. MindLynq members only.",
      },
    ],
  },
  {
    id: "billing",
    title: "Billing & Tiers",
    items: [
      {
        q: "What are the tiers?",
        a: "Signal (free) · Pulse ($9.99/mo) · Surge ($19.99/mo) · Spectrum ($29.99/mo) · Attuned ($99.99/mo, all 9 communities). Annual saves ~17%.",
      },
      {
        q: "Free trial?",
        a: "Every paid tier comes with a 7-day free trial. Cancel anytime in Account → Billing.",
      },
      {
        q: "How do I cancel?",
        a: "Account → Billing → Manage subscription. You'll be sent to your Stripe customer portal. Access continues through your billing period.",
      },
      {
        q: "Refund policy?",
        a: "Within 7 days of any purchase, request a refund from Account → Purchases → ⋯ → Request refund. Reviewed within 1 business day.",
      },
      {
        q: "What's à la carte?",
        a: "14 individual feature add-ons (Amplify, Priority Match, Replay packs, Voice Wave, Event Pass, etc.). One-time or monthly. Account → À la carte.",
      },
    ],
  },
  {
    id: "safety-tools",
    title: "Safety Tools",
    items: [
      {
        q: "How do I block someone?",
        a: "Open their profile or your conversation → ⋯ menu → Block. They can no longer see you, message you, or appear in your Discover. They are not notified.",
      },
      {
        q: "How do I report someone?",
        a: "Profile or message → ⋯ → Report. Choose a category (harassment, fake profile, threats, sexual content, etc.). Trust & Safety reviews within 24 hours.",
      },
      {
        q: "What is the Emergency Button?",
        a: "A floating button visible to all signed-in members. One tap dials your locale's emergency line, optionally records audio, and notifies your emergency contact. You can customize its appearance — color, label, position — but the function is always on.",
      },
      {
        q: "Will the other person know I blocked or reported them?",
        a: "No. Blocks and reports are silent on their end.",
      },
      {
        q: "What if I receive content I find harmful?",
        a: "Report it. If it depicts immediate harm, also use your Emergency Button. For illegal content (CSAM, threats, etc.) we report directly to NCMEC and law enforcement.",
      },
    ],
  },
  {
    id: "raela",
    title: "RAELA — your AI companion",
    items: [
      {
        q: "Who is RAELA?",
        a: "RAELA is Wavlynq's always-on AI companion. She helps draft messages, prep for dates, talk through tough moments, and surface safety resources. She's crisis-aware and free on every tier.",
      },
      {
        q: "Is RAELA reading my private messages?",
        a: "No. RAELA only sees what you type into her chat. Your DMs with other members are not shared with her unless you paste them in.",
      },
      {
        q: "What if I tell RAELA I'm in crisis?",
        a: "RAELA recognizes crisis language and immediately surfaces locale-appropriate hotlines and the Emergency Button before continuing the conversation.",
      },
      {
        q: "AI Training Contribution?",
        a: "Off by default. If you opt in (Account → AI training), anonymized chat snippets help improve RAELA. Opt out anytime — we delete your contribution from training sets within 30 days.",
      },
    ],
  },
  {
    id: "events-mentors",
    title: "Events & Mentors",
    items: [
      {
        q: "How do events work?",
        a: "Events appear in each app's Events tab. RSVP generates a barcode for check-in. ID-verified members only. Some events require an Event Pass à la carte purchase.",
      },
      {
        q: "Can I bring a guest?",
        a: "Yes — pre-add guests to your RSVP. Guests sign a liability waiver at check-in and present their own ID.",
      },
      {
        q: "What is a mentor?",
        a: "Mentors are members who have passed our triple background check (state, federal, sex-offender registry). They offer 1-on-1 paid sessions and free office hours. Available in all 3 apps.",
      },
      {
        q: "Is mentoring the same as therapy?",
        a: "No. Mentors are not licensed therapists (unless they explicitly say so on their profile). For clinical support, please see a licensed professional — start with the Crisis Resources above.",
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy & Data",
    items: [
      {
        q: "What data do you collect?",
        a: "What you give us (profile, messages, photos), what's needed to verify you (ID once, then deleted), and minimal usage analytics. Full detail at /privacy.",
      },
      {
        q: "Can I download my data?",
        a: "Yes. Account → Privacy → Export my data. We email you a JSON archive within 24 hours. (CCPA + GDPR.)",
      },
      {
        q: "Where are you based?",
        a: "Young's Digital Atelier LLC, San Antonio, Texas, USA. Subsidiary of Young's Multimedia Holdings LLC.",
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
// V2 community-specific help. Same shape as the trio: one community
// section + one safety/mentorship section. Tone matches V2 spec.
// ─────────────────────────────────────────────────────────────────
const ABLELYNQ_HELP: HelpSection[] = [
  {
    id: "ablelynq-community",
    title: "Ablelynq — Disability Community",
    description: "Built for the disability community — accessibility-first, peer-led.",
    items: [
      {
        q: "Who is Ablelynq for?",
        a: "Members of the disability community 18+ — physical, chronic illness, sensory, cognitive, invisible disabilities. You define your own identity; we don't gatekeep.",
      },
      {
        q: "How do you handle access needs?",
        a: "You can list access needs on your profile (mobility, sensory, communication, scheduling). Discovery, mentor matching, and event filters all respect them. Ableist behavior is a permanent ban.",
      },
      {
        q: "Are events accessible?",
        a: "Hosts must declare venue accessibility at creation (step-free, ASL, sensory-friendly, seating). Ablelynq events default to a higher accessibility bar; non-compliant events get rejected before going live.",
      },
      {
        q: "What if I'm overwhelmed?",
        a: "Use the Emergency Button → 'Pause my account 24h.' Snoozes notifications, hides you from Discover, pauses RAELA. Re-activate from Account anytime.",
      },
    ],
  },
];

const DEAFWAV_HELP: HelpSection[] = [
  {
    id: "deafwav-community",
    title: "Deafwav — Deaf & HoH Community",
    description: "Visual-first, ASL-aware, culturally Deaf.",
    items: [
      {
        q: "Who is Deafwav for?",
        a: "Deaf, Hard of Hearing, deafblind, late-deafened, and CODA members 18+. Hearing allies are welcome to support, not to swipe.",
      },
      {
        q: "Is the app ASL-friendly?",
        a: "Yes. Voice notes are auto-transcribed; you can record video intros in ASL; RAELA accepts text-only and never forces audio. All onboarding has captioned video walkthroughs.",
      },
      {
        q: "Do mentors sign?",
        a: "Filter mentors by 'Signs ASL' or 'Signs other (specify)' when browsing. Many Deafwav mentors are culturally Deaf themselves.",
      },
      {
        q: "What about audism?",
        a: "Audist behavior — dismissing Deaf culture, mocking signing, refusing to caption — is a reportable offense. Repeat offenses are a permanent ban.",
      },
    ],
  },
];

const FULLYNQ_HELP: HelpSection[] = [
  {
    id: "fullynq-community",
    title: "Fullynq — Plus-Size Community",
    description: "Body-affirming, real visibility, no apologies.",
    items: [
      {
        q: "Who is Fullynq for?",
        a: "Plus-size, mid-size, fat, and big-and-tall members 18+ who want connection without having to caveat their bodies.",
      },
      {
        q: "How do you handle fatphobia?",
        a: "Fatphobic language, 'fetish-only' messaging, and unsolicited diet/health commentary are reportable. Repeat offenses are a permanent ban. Admirers are fine; objectifiers are not.",
      },
      {
        q: "Are photos required?",
        a: "Same as every Wavlynq app — at least one verified face photo. Body shots are optional and never required. No 'headless torso' profiles.",
      },
      {
        q: "What if I'm having a hard body day?",
        a: "Use the Emergency Button → 'Pause my account 24h' to hide from Discover and pause RAELA outreach without losing matches.",
      },
    ],
  },
];

const JADESYNQ_HELP: HelpSection[] = [
  {
    id: "jadesynq-community",
    title: "Jadesynq — Pan-Asian Community",
    description: "Cultural pride, intergenerational, across the diaspora.",
    items: [
      {
        q: "Who is Jadesynq for?",
        a: "Members of the Pan-Asian diaspora 18+ — East Asian, Southeast Asian, South Asian, Central Asian, mixed, and adoptee members. You define your own heritage.",
      },
      {
        q: "How do you handle fetishization?",
        a: "Yellow fever / 'Asian preference' framing, exoticizing language, and racial fetish messaging are reportable. Repeat offenses are a permanent ban.",
      },
      {
        q: "Mentors who get the cultural pieces?",
        a: "Filter mentors by heritage and by 'Bilingual' / 'Family-of-origin navigation' / 'First-gen experience.' Intergenerational mentor pairings are a Jadesynq specialty.",
      },
      {
        q: "Language support?",
        a: "RAELA and inline translation work across major Asian languages. You can set your preferred language in Account.",
      },
    ],
  },
];

const OPENLYNQ_HELP: HelpSection[] = [
  {
    id: "openlynq-community",
    title: "Openlynq — Open Relationship Community",
    description: "Open, poly, ENM — clarity-first, judgment-free.",
    items: [
      {
        q: "Who is Openlynq for?",
        a: "Members 18+ practicing open, polyamorous, relationship-anarchist, swinging, or other ethically non-monogamous structures. Couples and solo members both welcome.",
      },
      {
        q: "Can I list partners?",
        a: "Yes. You can link a verified partner profile, declare hierarchy/structure, and disclose what you're looking for (dating, play, community, friendship). Discovery respects all of it.",
      },
      {
        q: "How do you handle consent and disclosure?",
        a: "Misrepresenting your relationship structure, hiding a partner, or violating disclosed agreements is reportable. Cheating-app behavior gets banned.",
      },
      {
        q: "Discretion?",
        a: "Incognito (free à la carte item) hides you from anyone outside your matches. You control who sees you.",
      },
    ],
  },
];

const SPIRITSYNQ_HELP: HelpSection[] = [
  {
    id: "spiritsynq-community",
    title: "Spiritsynq — Faith & Spirituality Community",
    description: "Reverent, mentor-led, practice-respectful.",
    items: [
      {
        q: "Who is Spiritsynq for?",
        a: "Members 18+ across faith and spiritual traditions — Christian, Muslim, Jewish, Hindu, Buddhist, Indigenous, Pagan, Spiritual-but-not-religious, and more. You define your own practice.",
      },
      {
        q: "How do you handle different traditions?",
        a: "You declare your tradition and how strict your practice is. Discovery, mentor matching, and events respect both. Proselytizing across traditions without consent is reportable.",
      },
      {
        q: "Are mentors clergy?",
        a: "Some mentors are credentialed clergy; many are lay practitioners. Filter by 'Clergy / formally trained' vs 'Peer practitioner.' All Wavlynq mentor BGC rules apply.",
      },
      {
        q: "Sabbath / prayer-time aware?",
        a: "Set quiet hours by tradition (Sabbath, Shabbat, Salah, daily prayer windows). RAELA, notifications, and event reminders honor them.",
      },
    ],
  },
];

export const APP_HELP: Record<AppId, HelpSection[]> = {
  fryqnc: [
    {
      id: "fryqnc-community",
      title: "Fryqnc — LGBTQIA+ Community",
      description: "Built for queer, trans, and non-binary members.",
      items: [
        {
          q: "Who is Fryqnc for?",
          a: "Members of the LGBTQIA+ community — gay, lesbian, bisexual, pansexual, queer, trans, non-binary, intersex, asexual, and questioning members 18+. Allies are welcome to support, not to swipe.",
        },
        {
          q: "How do you handle gender identity & pronouns?",
          a: "You can list any gender identity and any set of pronouns — typed in your own words, not picked from a dropdown. Members who misgender after being corrected can be reported under harassment.",
        },
        {
          q: "Do you support trans members specifically?",
          a: "Yes. ID verification accepts your legal name regardless of expression. We never out trans members — your trans status is yours to share. Transphobic behavior is a permanent ban.",
        },
        {
          q: "What if I'm in the closet?",
          a: "Use Incognito (free) to hide from Discover. Use a display name and photos that aren't on your other socials. We never sync contacts, never post anywhere, and never email anything identifying.",
        },
        {
          q: "Conversion-therapy content / TERF rhetoric?",
          a: "Permanent ban on first violation. Report → harassment → 'transphobia' or 'homophobia'. Reviewed same-day.",
        },
        {
          q: "Sensitive content moderation?",
          a: "All photos pass through automated moderation. Adult-content nudity is not allowed in profile photos but body-positive, pride, and gender-affirming photos absolutely are.",
        },
      ],
    },
  ],
  pulsynq: [
    {
      id: "pulsynq-community",
      title: "Pulsynq — Black Community",
      description: "Built for the Black diaspora.",
      items: [
        {
          q: "Who is Pulsynq for?",
          a: "Members of the Black diaspora — African, African-American, Afro-Caribbean, Afro-Latino, mixed-Black, and members of the broader Black community 18+.",
        },
        {
          q: "How do you handle race-based fetishization?",
          a: "Comments treating members as exotic, asking 'what are you,' or making race-based assumptions are reportable under harassment. Repeat offenses are permanent bans.",
        },
        {
          q: "Cultural events & gatherings?",
          a: "Pulsynq events lean toward culturally grounded experiences — cookouts, listening parties, book clubs, Juneteenth and Kwanzaa gatherings, mentorship circles. Hosted by verified community hosts.",
        },
        {
          q: "Mentor pairing?",
          a: "Mentor matching weights cultural fit and lived experience alongside specialty. You can filter mentors by community emphasis when browsing.",
        },
        {
          q: "Anti-Black behavior on the platform?",
          a: "Slurs, blackface imagery, racial profiling, or denial of identity are immediate permanent bans. Report → harassment → 'racism'.",
        },
        {
          q: "Diaspora connections beyond the US?",
          a: "Use the country selector and distance filter to find members in your region. Roaming (à la carte) lets you appear in another city's stack while traveling.",
        },
      ],
    },
  ],
  mindlynq: [
    {
      id: "mindlynq-community",
      title: "MindLynq — Neurodivergent Community",
      description: "Built for neurodivergent minds.",
      items: [
        {
          q: "Who is MindLynq for?",
          a: "Members who are neurodivergent — autistic, ADHD, dyslexic, dyspraxic, Tourette's, OCD, sensory processing, learning differences. Self-identification accepted; no diagnosis required.",
        },
        {
          q: "What is RESONATE and why is it MindLynq-only?",
          a: "RESONATE shares your communication preferences before you start talking — sensory needs, preferred message length, response-time expectations, what overwhelms you. It removes guesswork that's especially exhausting for neurodivergent members. Free with MindLynq.",
        },
        {
          q: "Sensory-aware design?",
          a: "MindLynq supports Low-Sensory Mode (no animations, muted colors, no autoplay) and Reduced Motion. Toggle in Account → Notifications → Low sensory mode.",
        },
        {
          q: "Notification batching?",
          a: "Set digest mode to Hourly or Daily. Set quiet hours so notifications never wake you. Mute individual senders without blocking them.",
        },
        {
          q: "Can I disable voice/video features?",
          a: "Yes. Account → Profile → toggle 'I prefer text only.' You won't be shown voice prompts and other members will see this preference on your profile.",
        },
        {
          q: "Mentors with lived ND experience?",
          a: "Filter mentors by 'Neurodivergent lived experience' when browsing. Many MindLynq mentors are autistic / ADHD themselves.",
        },
        {
          q: "What if I'm overwhelmed?",
          a: "Use the Emergency Button → 'Pause my account 24h.' This snoozes all notifications, hides you from Discover, and pauses RAELA outreach. Re-activate from Account anytime.",
        },
      ],
    },
  ],
  ablelynq: ABLELYNQ_HELP,
  deafwav: DEAFWAV_HELP,
  fullynq: FULLYNQ_HELP,
  jadesynq: JADESYNQ_HELP,
  openlynq: OPENLYNQ_HELP,
  spiritsynq: SPIRITSYNQ_HELP,
};

// Quick lookup of every Q+A across all sections (for RAELA / search later)
export const ALL_HELP_ITEMS = (): Array<HelpItem & { section: string }> => {
  const out: Array<HelpItem & { section: string }> = [];
  PLATFORM_HELP.forEach((s) =>
    s.items.forEach((i) => out.push({ ...i, section: s.title })),
  );
  (Object.values(APP_HELP).flat() as HelpSection[]).forEach((s) =>
    s.items.forEach((i) => out.push({ ...i, section: s.title })),
  );
  return out;
};
